import React, { useEffect, useState } from "react";
import { Heart, Trash2, Plus, FolderHeart, CloudCheck, Cloud } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import PlaceCard from "@/components/PlaceCard";
import ShareButton from "@/components/ShareButton";

function useResolvePlaces(ids) {
  const [places, setPlaces] = useState([]);
  useEffect(() => {
    if (!ids.length) { setPlaces([]); return; }
    api.get("/places-by-ids", { params: { ids: ids.join(",") } }).then((r) => setPlaces(r.data)).catch(() => setPlaces([]));
  }, [ids.join(",")]);
  return places;
}

export default function Favorites() {
  const { t, favorites, lists, createList, deleteList, user, synced, syncStatus, retrySync } = useApp();
  const navigate = useNavigate();
  const favPlaces = useResolvePlaces(favorites);
  const [newName, setNewName] = useState("");
  const [showInput, setShowInput] = useState(false);

  const handleCreate = () => {
    const n = newName.trim();
    if (n) { createList(n); setNewName(""); setShowInput(false); }
  };

  const empty = favorites.length === 0 && lists.length === 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-extrabold text-ink">{t("favorites")}</h1>
        <button data-testid="new-list-toggle" onClick={() => setShowInput((s) => !s)} className="inline-flex items-center gap-1.5 rounded-full bg-terracotta px-3.5 py-2 text-sm font-bold text-white">
          <Plus className="h-4 w-4" /> {t("create_list")}
        </button>
      </div>

      {user && synced ? (
        <p data-testid="sync-status" className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700"><CloudCheck className="h-4 w-4" /> {t("synced")}</p>
      ) : user ? <div data-testid="sync-pending-status" className="flex flex-wrap items-center gap-2 text-xs text-sea"><Cloud className="h-4 w-4" />{t(syncStatus === "error" ? "sync_error" : "syncing")}{syncStatus === "error" && <button data-testid="sync-retry" onClick={retrySync} className="font-semibold underline">{t("sync_retry")}</button>}</div> : !user && !empty ? (
        <button data-testid="sync-hint" onClick={() => navigate("/profile")} className="inline-flex items-center gap-1.5 text-left text-xs font-semibold text-sea underline-offset-2 hover:underline"><Cloud className="h-4 w-4" /> {t("sync_hint")}</button>
      ) : null}

      {showInput && (
        <div className="flex items-center gap-2">
          <input data-testid="fav-new-list-input" maxLength={160} value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleCreate()} placeholder={t("list_name")} className="min-w-0 flex-1 rounded-xl border border-border bg-white px-3.5 py-2.5 text-sm outline-none focus:border-terracotta" />
          <button data-testid="fav-create-list" onClick={handleCreate} className="rounded-xl bg-terracotta px-4 py-2.5 text-sm font-bold text-white">{t("create")}</button>
        </div>
      )}

      {empty ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-3xl border border-dashed border-border bg-white py-16 text-center" data-testid="favorites-empty">
          <Heart className="h-10 w-10 text-terracotta/40" />
          <p className="font-heading font-bold text-ink">{t("empty_fav_title")}</p>
          <p className="max-w-xs text-sm text-ink-muted">{t("empty_fav_text")}</p>
        </div>
      ) : (
        <>
          {favorites.length > 0 && (
            <section>
              <div className="mb-3 flex items-center justify-between">
                <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-ink"><Heart className="h-5 w-5 text-terracotta" /> {t("my_favorites")}</h2>
                <ShareButton title={t("my_favorites")} placeIds={favorites} testid="share-favorites-btn" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                {favPlaces.map((p) => <PlaceCard key={p.id} place={p} compact />)}
              </div>
            </section>
          )}
          {lists.map((l) => <ListSection key={l.id} list={l} onDelete={() => deleteList(l.id)} />)}
        </>
      )}
    </div>
  );
}

function ListSection({ list, onDelete }) {
  const { t } = useApp();
  const places = useResolvePlaces(list.placeIds);
  return (
    <section data-testid={`fav-list-${list.id}`}>
      <div className="mb-3 flex items-center justify-between">
        <h2 data-testid={`list-name-${list.id}`} className="flex min-w-0 items-center gap-2 font-heading text-lg font-bold text-ink"><FolderHeart className="h-5 w-5 shrink-0 text-sea" /><span className="break-words [overflow-wrap:anywhere]">{list.name}</span></h2>
        <div className="flex shrink-0 items-center gap-1">
          {list.placeIds.length > 0 && <ShareButton title={list.name} placeIds={list.placeIds} testid={`share-list-${list.id}`} size="h-8 w-8" />}
          <button data-testid={`delete-list-${list.id}`} onClick={onDelete} className="grid h-8 w-8 place-items-center rounded-full text-ink-muted hover:bg-red-50 hover:text-red-500">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
      {places.length === 0 ? (
        <p className="rounded-xl bg-sand px-4 py-6 text-center text-sm text-ink-muted">{t("empty_fav_text")}</p>
      ) : (
        <div className="grid grid-cols-2 gap-4">{places.map((p) => <PlaceCard key={p.id} place={p} compact />)}</div>
      )}
    </section>
  );
}
